import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useLogicModel, STEPS_ORDER, STEP_LABELS, WizardStep } from '../../contexts/LogicModelContext';
import { ImpactGoalStep } from './steps/ImpactGoalStep';
import { OutcomesStep } from './steps/OutcomesStep';
import { OutputsStep } from './steps/OutputsStep';
import { ActivitiesStep } from './steps/ActivitiesStep';
import { InputsStep } from './steps/InputsStep';
import { ReviewStep } from './steps/ReviewStep';
import { TemplateLibrary, Template } from './TemplateLibrary';

const STEP_ICONS: Record<WizardStep, string> = {
  impact: '🎯',
  outcomes: '📈',
  outputs: '📊',
  activities: '⚡',
  inputs: '📦',
  review: '✅',
};

export function LogicModelWizard() {
  const { programId, logicModelId } = useParams<{ programId: string; logicModelId?: string }>();
  const navigate = useNavigate();
  const [showTemplates, setShowTemplates] = useState(false);
  const {
    logicModel,
    currentStep,
    completedSteps,
    isLoading,
    isSaving,
    error,
    setCurrentStep,
    nextStep,
    prevStep,
    canNavigateTo,
    initializeLogicModel,
    applyTemplate,
  } = useLogicModel();

  useEffect(() => {
    if (programId) {
      initializeLogicModel(programId, logicModelId);
    }
  }, [programId, logicModelId, initializeLogicModel]);

  const handleTemplateSelect = async (template: Template) => {
    await applyTemplate(template);
    setShowTemplates(false);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading Logic Model...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-2xl mx-auto mt-8">
        <div className="bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-lg">
          <h3 className="font-medium mb-2">Error</h3>
          <p>{error}</p>
          <Link
            to={`/programs/${programId}`}
            className="mt-4 inline-block text-red-600 hover:text-red-800 underline"
          >
            ← Back to Program
          </Link>
        </div>
      </div>
    );
  }

  const currentStepIndex = STEPS_ORDER.indexOf(currentStep);

  const renderStep = () => {
    switch (currentStep) {
      case 'impact':
        return <ImpactGoalStep />;
      case 'outcomes':
        return <OutcomesStep />;
      case 'outputs':
        return <OutputsStep />;
      case 'activities':
        return <ActivitiesStep />;
      case 'inputs':
        return <InputsStep />;
      case 'review':
        return <ReviewStep />;
      default:
        return null;
    }
  };

  return (
    <div className="max-w-5xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Link
              to={`/programs/${programId}`}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {logicModel?.name || 'Logic Model'}
              </h1>
              <p className="text-sm text-gray-500">
                {logicModel?.status === 'draft' && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                    Draft
                  </span>
                )}
              </p>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            {/* Template button */}
            <button
              onClick={() => setShowTemplates(true)}
              className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md flex items-center"
            >
              <span className="mr-1">📋</span>
              Templates
            </button>

            {/* Save indicator */}
            {isSaving && (
              <div className="flex items-center text-sm text-gray-500">
                <svg className="animate-spin h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Saving...
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Stepper */}
      <div className="mb-8">
        <nav aria-label="Progress">
          <ol className="flex items-center">
            {STEPS_ORDER.map((step, index) => {
              const isComplete = completedSteps.includes(step);
              const isCurrent = step === currentStep;
              const canClick = canNavigateTo(step);
              
              return (
                <li key={step} className={`relative ${index !== STEPS_ORDER.length - 1 ? 'pr-8 sm:pr-20 flex-1' : ''}`}>
                  {/* Connector line */}
                  {index !== STEPS_ORDER.length - 1 && (
                    <div className="absolute top-4 left-0 w-full h-0.5 bg-gray-200" aria-hidden="true">
                      <div
                        className={`h-full transition-all duration-300 ${
                          isComplete || STEPS_ORDER.indexOf(currentStep) > index ? 'bg-blue-600' : 'bg-gray-200'
                        }`}
                        style={{ width: isComplete || STEPS_ORDER.indexOf(currentStep) > index ? '100%' : '0%' }}
                      />
                    </div>
                  )}
                  
                  {/* Step button */}
                  <button
                    onClick={() => canClick && setCurrentStep(step)}
                    disabled={!canClick}
                    className={`relative flex flex-col items-center group ${canClick ? 'cursor-pointer' : 'cursor-not-allowed'}`}
                  >
                    <span
                      className={`w-8 h-8 flex items-center justify-center rounded-full text-sm font-medium transition-colors ${
                        isCurrent
                          ? 'bg-blue-600 text-white ring-2 ring-blue-600 ring-offset-2'
                          : isComplete
                          ? 'bg-blue-600 text-white'
                          : canClick
                          ? 'bg-gray-200 text-gray-600 group-hover:bg-gray-300'
                          : 'bg-gray-100 text-gray-400'
                      }`}
                    >
                      {isComplete && !isCurrent ? (
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      ) : (
                        <span>{STEP_ICONS[step]}</span>
                      )}
                    </span>
                    <span
                      className={`mt-2 text-xs font-medium hidden sm:block ${
                        isCurrent ? 'text-blue-600' : 'text-gray-500'
                      }`}
                    >
                      {STEP_LABELS[step]}
                    </span>
                  </button>
                </li>
              );
            })}
          </ol>
        </nav>
      </div>

      {/* Step content */}
      <div className="bg-white shadow rounded-lg p-6 mb-6">
        {renderStep()}
      </div>

      {/* Navigation buttons */}
      <div className="flex justify-between">
        <button
          onClick={prevStep}
          disabled={currentStepIndex === 0}
          className={`px-4 py-2 text-sm font-medium rounded-md ${
            currentStepIndex === 0
              ? 'text-gray-400 cursor-not-allowed'
              : 'text-gray-700 hover:bg-gray-100'
          }`}
        >
          ← Previous
        </button>
        
        <div className="flex space-x-3">
          <Link
            to={`/programs/${programId}`}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md"
          >
            Save & Exit
          </Link>
          
          {currentStepIndex < STEPS_ORDER.length - 1 ? (
            <button
              onClick={nextStep}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md"
            >
              Next →
            </button>
          ) : (
            <button
              onClick={() => navigate(`/programs/${programId}`)}
              className="px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-md"
            >
              Complete ✓
            </button>
          )}
        </div>
      </div>

      {/* Template Library Modal */}
      {showTemplates && (
        <TemplateLibrary
          onSelect={handleTemplateSelect}
          onClose={() => setShowTemplates(false)}
        />
      )}
    </div>
  );
}
